<template>
  <app-layout>
    <template #header>
      <div class="flex items-center">
        <div class="flex-1">
          <h1 class="text-lg flex items-center">
            <span
              v-if="state.project.stream && !isSingleStream"
              class="border rounded px-2 py-1 align-center text-xs inline-block leading-none mr-2"
              :style="getStreamColorsByKey(state.project.stream)"
            >
              {{ getStreamNameByKey(state.project.stream) }}
            </span>
            {{ state.project.ref }}
          </h1>
          <p class="text-grey-700 hidden sm:block text-sm">
            {{ state.competition.title }} Form
          </p>
        </div>
        <div class="ml-6 self-center">
          <template
            v-if="
              !state.project.submitted_at &&
                (state.competition.statuses.open || hasExtension)
            "
          >
            <a
              :href="route('home')"
              class="transition-all transition-normal inline-flex items-center px-4 py-3 border-2 border-grey-300 text-grey-800 shadow text-sm font-semibold bg-white hover:bg-gray-50 focus:outline-none focus:shadow-outline no-underline hover:bg-primary-100 hover:border-primary-300 leading-none mr-2"
              @click.prevent="saveApplicationButton"
            >
              Save & Exit
            </a>
            <button
              type="button"
              class="transition-all transition-normal inline-flex items-center px-4 py-3 border-2 border-transparent shadow text-sm font-semibold text-white bg-primary-500 hover:bg-primary-700 hover:text-white leading-none focus:outline-none focus:shadow-outline no-underline ml-2"
              @click="showConfirmation"
            >
              Submit
            </button>
          </template>
          <template v-else>
            <a
              :href="route('home')"
              class="transition-all transition-normal inline-flex items-center px-4 py-3 border-2 border-grey-300 text-grey-800 shadow text-sm font-semibold bg-white hover:bg-gray-50 focus:outline-none focus:shadow-outline no-underline hover:bg-primary-100 hover:border-primary-300 leading-none mr-2"
            >
              Exit to Dashboard
            </a>
          </template>
        </div>
      </div>
    </template>
    <div class="container">
      <div class="flex items-center justify-between border-b border-grey-200">
        <ul class="list-reset flex -mb-0.5">
          <li v-for="(tab, index) in tabs" :key="index" class="mr-8">
            <a
              :href="'#' + tab.hash"
              class="inline-block py-4 border-b-2 -mb-0.5 no-underline"
              :class="{
                'border-primary-500 text-primary-600 font-semibold':
                  active === index,
                'border-transparent text-grey-600 hover:text-grey-900 hover:border-grey-300':
                  active !== index
              }"
              @click.stop.prevent="setActive(index)"
            >
              {{ tab.name }}
            </a>
          </li>
        </ul>
      </div>
    </div>
    <div class="container">
      <div
        v-if="selectedAssessmentPhase"
        class="max-w-lg mx-auto py-8"
      >
        <template
          v-for="(entry, section) in selectedAssessmentPhase.assessor_summary"
        >
          <div
            v-if="Object.values(entry).length"
            :key="section"
            class="mb-8 pb-6 bg-white p-6"
          >
            <h2 class="text-xl font-semibold mb-4 border-b-2 border-grey-200 pb-4">
              {{ section }}
            </h2>

            <dl>
              <div
                v-for="(item, index) in entry"
                :key="index"
                class="mb-6"
                :class="{
                  'border-b-2 border-grey-200 pb-6': Object.keys(entry).length > 1
                }"
              >
                <dt class="text-lg font-semibold mb-4">
                  {{ item.question }}
                </dt>
                <dd
                  v-for="(answer, ai) in item.answers"
                  :key="ai"
                  class="mb-2"
                >
                  <strong>Assessor {{ ai + 1 }}</strong>
                  <div v-html="parseMarkdown(answer, false)" />
                </dd>
              </div>
            </dl>
          </div>
        </template>
      </div>
      <div v-else class="py-8 text-center text-grey-600">
        <p class="text-lg">
          No feedback is available for this application at this time.
        </p>
      </div>
      <ul class="list-reset flex justify-between items-baseline">
        <li class="w-1/3">
          <icon-link
            v-if="active > 0"
            :href="'#' + tabs[active - 1].hash"
            icon="arrow-thin-left-circle"
            @click.stop.prevent="setActive(active - 1)"
          >
            Previous: {{ tabs[active - 1].name }}
          </icon-link>
        </li>
        <li class="w-1/3 text-center">
          <icon-link
            :href="route('home')"
            class="text-positive-600 hover:text-positive-800"
          >
            Dashboard
          </icon-link>
        </li>
        <li class="w-1/3 text-right">
          <icon-link
            v-if="active + 1 < tabs.length"
            :href="'#' + tabs[active + 1].hash"
            icon="arrow-thin-right-circle"
            icon-placement="right"
            @click.stop.prevent="setActive(active + 1)"
          >
            Next: {{ tabs[active + 1].name }}
          </icon-link>
        </li>
      </ul>
    </div>
  </app-layout>
</template>

<script>
import kebabCase from "lodash/kebabCase";
import { format, isBefore, parseISO } from "date-fns";
import { requestMixin } from "../mixins/request";
import { competitionStreamsMixin } from "../mixins/competitionStreams";
export default {
  name: "ApplicationFeedbackView",
  mixins: [requestMixin, competitionStreamsMixin],
  data() {
    return {
      active: 0
    };
  },
  computed: {
    competitionYear() {
      return format(
        parseISO(this.state.competition.application_start_date),
        "yyyy"
      );
    },
    competitionHash() {
      return kebabCase(this.state.competition.title);
    },
    singleStageGrading() {
      return this.state.competition.single_stage_grading === 1;
    },
    showFeedbackTab() {
      return this.singleStageGrading
        ? this.state.project.awarded_at ||
            this.state.project.rejected_at ||
            this.state.project.shortlisted_at
        : this.state.project.feedback;
    },
    /**
     * competitionForm: checks whether it exisits and parses the JSON
     * @returns	{object}			The parsed json, if it exisits
     */
    competitionForm() {
      if (!this.state.competition.application_template) return [];
      return JSON.parse(this.state.competition.application_template);
    },
    /**
     * fieldsets: checks that the application form has fieldsets
     * @returns	{array}			Returns and array of fieldsets
     */
    fieldsets() {
      if (!this.competitionForm.fieldset) return [];
      return this.competitionForm.fieldset;
    },

    /**
     * assessmentPhases: returns the assessment phases from the state
     * @returns	{array}		returns the assessment phases
     */
    assessmentPhases() {
      return this.state.assessment_phases || [];
    },

    /**
     * feedback: returns the feedback from the state
     * @returns	{array}		returns the feedback
     */
    feedback() {
      return this.state.feedback || [];
    },
    /**
     * tabs: used to create usable data for each of the tabs
     * @returns	{array}		contains each of the fieldset's name and hash
     */
    tabs() {
      if (!this.assessmentPhases) return [];
      return this.assessmentPhases.map(item =>
        Object.assign(
          {},
          {
            name: item.name,
            hash: this.hashFriendly(item.name)
          }
        )
      );
    },

    /**
     * selectedAssessmentPhase: returns the currently active assessment phase
     * @returns	{object}		returns the active assessment phase
     */
    selectedAssessmentPhase() {
      return (
        this.feedback.find(
          phase =>
            phase.marking_phase.id === this.assessmentPhases[this.active].id
        ) ?? null
      );
    },

    now() {
      return new Date();
    },
    hasPassed() {
      if (this.state.project.application_extended_until !== null) {
        return isBefore(this.application_extended_until, this.now);
      }
      return !this.state.competition.statuses.open;
    },
    application_end_date() {
      return parseISO(this.state.competition.application_end_date);
    },
    application_extended_until() {
      if (!this.state.project.application_extended_until) return;
      return parseISO(this.state.project.application_extended_until);
    },
    hasExtension() {
      return !!this.application_extended_until && !this.hasPassed;
    },
    projectNotices() {
      const notice = [];
      if (this.hasExtension && !this.state.project.submitted_at) {
        const extensionDate = this.format(
          this.application_extended_until,
          "do MMMM yyyy hh:mm"
        );
        notice.push({
          type: "neutral",
          content: `Your application has been extended until ${extensionDate}`
        });
      } else if (
        !this.state.project.submitted_at &&
        !this.state.competition.statuses.open
      ) {
        notice.push({
          type: "neutral",
          content: `${this.state.competition.title} is closed for submission`
        });
      } else if (this.state.project.rejected_at) {
        notice.push({
          type: "negative",
          content: `Your ${this.state.competition.title} was unsuccessful`
        });
      } else if (this.state.project.invited_at) {
        notice.push({
          type: "positive",
          content: `Your ${this.state.competition.title} was successful`
        });
      } else if (this.state.project.submitted_at) {
        notice.push({
          type: "neutral",
          content: `Your ${this.state.competition.title} is under review`
        });
      }
      return notice;
    }
  },
  created() {
    document.title = `${this.state.project.ref} Application Feedback :: ${document.title}`;

    this.loadTabWithHash(window.location.hash);
  },
  methods: {
    loadTabWithHash(hash) {
      if (!hash) return;
      hash = hash.replace("#", "");
      const index =
        hash === "feedback"
          ? "feedback"
          : this.tabs.findIndex(tab => tab.hash === hash);
      if (index < 0) return;
      return this.setActive(index);
    },
    /**
     * hashFriendly: creates a url friendly string of the value
     * @param		{string}	value	the value that will be "Kebabbed"
     * @return	{string}				the kebabbed string e.g. "this-is-kebabbed"
     */
    hashFriendly(value) {
      return kebabCase(value);
    },
    /**
     * setActive: used to set the the active fieldset of the application
     * @param	{int}	index	the index of the fieldset
     */
    setActive(index) {
      window.location.hash = this.tabs[index] ? this.tabs[index].hash : index;
      this.scrollTop();
      this.saveApplication;
      this.active = index;
    }
  }
};
</script>
